import * as React from 'react'
import * as Moment from 'moment'
import { Row, Col, InputGroup, InputGroupText, Input, Card, CardBody} from 'reactstrap';
import { BadgeContainer, Badge as Badge2 } from '@progress/kendo-react-indicators';
import { EditorUtils, Editor, EditorTools, ProseMirror } from '@progress/kendo-react-editor'
import { LuBellRing, LuBellOff, LuLoaderCircle } from 'react-icons/lu';
import { Button } from "@progress/kendo-react-buttons";
import { marks, nodes, Schema } from '@progress/kendo-editor-common';
import { accentUtils, t, formHelper, showOK, promise, showDialog, A, from, format } from '../services/HelperService'
import { AccentSpinner } from './AccentSpinner'
import { AccentSectionHeader } from './AccentSectionHeader'
import AccentIcons from '../icons/Icons'
import { SelectSketchDlg } from '../dialogs/SelectSketchDlg'
import { queryNoTracking, update } from '../services/DataService'
import { getAccess, getUser } from '../services/UserService';
import { RTLSearchSelectCtrl } from './RTLSearchSelectCtrl';
import GRIDS from '../grids/_GridDefinitions';
import { AccentPopup } from '../dialogs/AccentPopup';
import { AlertsDisplay, useFileNoteAlerts } from '../app-framework/FileNoteAlerts';
import { isMobileOnly } from './AccentDisplay';
import { RTLQueryFetcher } from './RTLFetch';


const querySketch = queryNoTracking("Sketch", 0);
const query_Representative_ActiveGrid = queryNoTracking("Representative_ActiveGrid");
const query_FileNoteAlertsForContactCompany = queryNoTracking("FileNoteAlertsForContactCompany", 0);
const query_FileNote_TagOptions = queryNoTracking("FileNote_TagOptions", 0);
const { EditorState } = ProseMirror;

function canEditFileNote(fileNote) {

    var user = getUser();

    if (fileNote.SystemGenerated) return false;

    if (user.ID === fileNote.RepresentativeID) return true;

    if (getAccess().features().FeatureAllowSetup()) return true;

    if (getAccess().features().FeatureAllowManagement()) return true;

    
    return false;
}

const hasFileNoteContent = html => {
    if (accentUtils.isEmpty(html)) return false;

    if (typeof document === "undefined") {
        return !accentUtils.isEmpty(String(html).replace(/<[^>]*>/g, "")) || /<img\b/i.test(html);
    }

    const element = document.createElement("div");
    element.innerHTML = html;

    return !accentUtils.isEmpty(element.textContent) || !accentUtils.isNull(element.querySelector("img"));
};

const splitFileNoteTags = tagName => Array.from(new Set((tagName ?? "")
    .split(',')
    .map(tag => tag.trim())
    .filter(tag => !accentUtils.isNullOrEmpty(tag))));

const COSTCO_FILE_NOTE_TAG = "file_note_costco";

const hasFileNoteTag = (tagName, tag) => splitFileNoteTags(tagName)
    .some(t => t.toLowerCase() === tag.toLowerCase());

const FileNoteTag = React.memo(({ tagName, onClick }) => {
    const tags = splitFileNoteTags(tagName);

    if (tags.length === 0) return null;

    return tags.map(tag => (
        <span
            key={tag}
            title={t(tag)}
            onClick={onClick}
            style={{
                marginLeft: '5px',
                fontSize: '11px',
                lineHeight: '1.2',
                verticalAlign: 'baseline',
                backgroundColor: '#0d6efd',
                color: 'white',
                borderRadius: '3px',
                padding: '1px 4px',
                fontWeight: 600,
                cursor: onClick ? 'pointer' : undefined
            }}
        >
            {t(tag)}
        </span>
    ));
});

const FileNoteCostcoSyncStatus = React.memo(({ fileNote }) => {
    if (!hasFileNoteTag(fileNote.Tag, COSTCO_FILE_NOTE_TAG)) return null;

    const status = fileNote.CostcoLeadNoteSyncStatus ?? "Pending";
    const colors = {
        Pending: "#6c757d",
        Failed: "#dc3545",
        Synced: "#198754",
        Cancelled: "#6c757d"
    };
    const title = status === "Failed" && !accentUtils.isNullOrEmpty(fileNote.CostcoLeadNoteSyncError)
        ? fileNote.CostcoLeadNoteSyncError
        : `Costco note sync: ${status}`;

    return (
        <span
            title={title}
            style={{
                marginLeft: "5px",
                fontSize: "11px",
                lineHeight: "1.2",
                verticalAlign: "baseline",
                backgroundColor: colors[status] ?? "#6c757d",
                color: "white",
                borderRadius: "3px",
                padding: "1px 4px",
                fontWeight: 600
            }}
        >
            {status}
        </span>
    );
});

const FileNoteTagSelector = React.memo(({ fileNote, onTagsChanged }) => {
    const anchor = React.useRef(null);
    const [open, setOpen] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [options, setOptions] = React.useState([]);
    const currentTags = splitFileNoteTags(fileNote.Tag);

    const loadOptions = React.useCallback(async () => {
        setLoading(true);
        try {
            const rows = await query_FileNote_TagOptions.getAllWithCache();
            setOptions(Array.isArray(rows) ? rows : []);
        } catch (error) {
            console.error("Error loading file note tags:", error);
            setOptions([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const openSelector = React.useCallback(() => {
        setOpen(true);
        loadOptions();
    }, [loadOptions]);

    const closeSelector = React.useCallback(() => {
        setOpen(false);
    }, []);

    const saveTags = React.useCallback(async tags => {
        const safeTags = splitFileNoteTags((tags ?? []).join(","));

        await update("FileNote_SetTags", { ID: fileNote.ID, Tags: safeTags }, true);
        query_FileNote_TagOptions.clearCache();
        onTagsChanged(fileNote.ID, safeTags.join(","));
        closeSelector();
    }, [fileNote.ID, onTagsChanged, closeSelector]);

    const addTag = React.useCallback(tag => {
        const safeTag = (tag ?? "").trim();
        if (accentUtils.isNullOrEmpty(safeTag)) return;

        saveTags([...currentTags, safeTag]);
    }, [currentTags, saveTags]);

    const removeTag = React.useCallback(tag => {
        saveTags(currentTags.filter(t => t.toLowerCase() !== (tag ?? "").toLowerCase()));
    }, [currentTags, saveTags]);

    if (fileNote.SystemGenerated) return <FileNoteTag tagName={fileNote.Tag} />;

    const availableOptions = from(options)
        .where(o => !currentTags.some(tag => tag.toLowerCase() === (o.Tag ?? "").toLowerCase()))
        .toArray();

    return (
        <span style={{ display: "inline-block", marginLeft: "5px", fontSize: "smaller" }}>
            <span ref={anchor}>
                {currentTags.length > 0 ? <FileNoteTag tagName={fileNote.Tag} onClick={openSelector} /> : <>[<span className="acc-link" onClick={openSelector}>Tag</span>]</>}
            </span>
            <AccentPopup
                className="jobWatcherPopup"
                show={open}
                onAutoClose={closeSelector}
                anchor={anchor}
                popupAlign={{ horizontal: "left", vertical: "top" }}
                anchorAlign={{ horizontal: "left", vertical: "bottom" }}
                headerContent={<strong>{t("Tag File Note")}</strong>}
            >
                <div style={{ minWidth: "260px" }}>
                    {currentTags.length > 0 && (
                        <div style={{ marginBottom: "8px" }}>
                            <div style={{ fontWeight: 600, marginBottom: "4px" }}>{t("Current Tags")}</div>
                            {currentTags.map(tag => (
                                <span
                                    key={tag}
                                    style={{
                                        display: "inline-block",
                                        marginRight: "5px",
                                        marginBottom: "5px",
                                        backgroundColor: "#0d6efd",
                                        color: "white",
                                        borderRadius: "3px",
                                        padding: "2px 5px",
                                        fontWeight: 600
                                    }}
                                >
                                    {t(tag)} <span className="acc-link" style={{ color: "white" }} onClick={() => removeTag(tag)}>x</span>
                                </span>
                            ))}
                        </div>
                    )}
                    <div style={{ maxHeight: "220px", overflowY: "auto" }}>
                        {loading && <div style={{ padding: "6px" }}>{t("Loading...")}</div>}
                        {!loading && availableOptions.map(option => (
                            <div
                                key={option.Tag}
                                className="acc-link"
                                style={{ padding: "6px" }}
                                onClick={() => addTag(option.Tag)}
                            >
                                {t(option.Text ?? option.Tag)}
                            </div>
                        ))}
                        {!loading && availableOptions.length === 0 && <div style={{ padding: "6px" }}>{t("No tags available")}</div>}
                    </div>
                </div>
            </AccentPopup>
        </span>
    );
});

const toDateInputValue = date => {
    return Moment(date).format("YYYY-MM-DD");
};

// File note dates are stored as UTC instants and displayed in the browser's time zone
// (format.formatDateTime), so day boundaries for the filter are taken in the browser's
// time zone too. The moment is already browser-local; toISOString() gives the UTC instant.
const toUtcIsoFromUserTimeZone = momentValue => momentValue.toDate().toISOString();

const getFileNoteDateRange = key => {
    const start = Moment().startOf("day");
    const end = Moment(start).add(1, "day");

    if (key === "today") {
        return { label: "Today", dateFrom: toUtcIsoFromUserTimeZone(start), dateTo: toUtcIsoFromUserTimeZone(end) };
    }

    if (key === "week") {
        const weekStart = Moment().startOf("isoWeek");
        const weekEnd = Moment(weekStart).add(1, "week");
        return { label: "This Week", dateFrom: toUtcIsoFromUserTimeZone(weekStart), dateTo: toUtcIsoFromUserTimeZone(weekEnd) };
    }

    if (key === "last14") {
        const from = Moment().startOf("day").subtract(13, "days");
        return { label: "Last 14 Days", dateFrom: toUtcIsoFromUserTimeZone(from), dateTo: toUtcIsoFromUserTimeZone(end) };
    }

    if (key === "month") {
        const monthStart = Moment().startOf("month");
        const monthEnd = Moment(monthStart).add(1, "month");
        return { label: "This Month", dateFrom: toUtcIsoFromUserTimeZone(monthStart), dateTo: toUtcIsoFromUserTimeZone(monthEnd) };
    }

    if (key === "year") {
        const yearStart = Moment().startOf("year");
        const yearEnd = Moment(yearStart).add(1, "year");
        return { label: "This Year", dateFrom: toUtcIsoFromUserTimeZone(yearStart), dateTo: toUtcIsoFromUserTimeZone(yearEnd) };
    }

    return null;
};

const getCustomFileNoteDateRange = (customFrom, customTo) => {
    const hasCustomFrom = !accentUtils.isNullOrEmpty(customFrom);
    const hasCustomTo = !accentUtils.isNullOrEmpty(customTo);

    if (!hasCustomFrom && !hasCustomTo) return null;

    return {
        label: "Custom",
        dateFrom: hasCustomFrom ? toUtcIsoFromUserTimeZone(Moment(customFrom).startOf("day")) : null,
        dateTo: hasCustomTo ? toUtcIsoFromUserTimeZone(Moment(customTo).startOf("day").add(1, "day")) : null
    };
};

const FileNoteDateFilterPreview = React.memo(({ btnStyle, dateFilter, onChange }) => {
    const anchor = React.useRef(null);
    const [open, setOpen] = React.useState(false);
    const [customFrom, setCustomFrom] = React.useState("");
    const [customTo, setCustomTo] = React.useState("");

    const options = [
        { key: null, label: "All", title: "All Dates" },
        { key: "today", label: "Today", title: "Today" },
        { key: "week", label: "This Week", title: "This Week" },
        { key: "last14", label: "Last 14 Days", title: "Last 14 Days" },
        { key: "month", label: "This Month", title: "This Month" },
        { key: "year", label: "This Year", title: "This Year" }
    ];

    const selectOption = option => {
        onChange(option.key ? getFileNoteDateRange(option.key) : null);
        setOpen(false);
    };

    const applyCustom = () => {
        const hasCustom = !accentUtils.isNullOrEmpty(customFrom) || !accentUtils.isNullOrEmpty(customTo);

        if (!hasCustom) {
            onChange(null);
            setOpen(false);
            return;
        }

        onChange(getCustomFileNoteDateRange(customFrom, customTo));
        setOpen(false);
    };

    const label = dateFilter?.label ?? "Date";

    return (
        <InputGroupText>
            <span ref={anchor}>
                <Button style={btnStyle} title="Date Filter" onClick={() => setOpen(true)}>{label}</Button>
            </span>
            <AccentPopup
                className="jobWatcherPopup"
                show={open}
                onAutoClose={() => setOpen(false)}
                anchor={anchor}
                popupAlign={{ horizontal: "left", vertical: "top" }}
                anchorAlign={{ horizontal: "left", vertical: "bottom" }}
                headerContent={<strong>{t("Date Filter")}</strong>}
            >
                <div style={{ minWidth: "260px" }}>
                    {options.map(option => (
                        <div
                            key={option.label}
                            className="acc-link"
                            title={option.title}
                            style={{ padding: "6px" }}
                            onClick={() => selectOption(option)}
                        >
                            {option.label}
                        </div>
                    ))}
                    <div style={{ borderTop: "1px solid #ddd", marginTop: "6px", paddingTop: "8px" }}>
                        <div style={{ fontWeight: 600, marginBottom: "6px" }}>{t("Custom")}</div>
                        <Input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={{ marginBottom: "6px" }} />
                        <Input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} style={{ marginBottom: "6px" }} />
                        <Button style={btnStyle} onClick={applyCustom}>{t("Apply")}</Button>
                    </div>
                </div>
            </AccentPopup>
        </InputGroupText>
    );
});

const FileNoteTagFilterPreview = React.memo(({ btnStyle, tagFilter, onChange }) => {
    const anchor = React.useRef(null);
    const [open, setOpen] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [options, setOptions] = React.useState([]);
    const selectedTags = tagFilter?.tags ?? [];

    const loadOptions = React.useCallback(async () => {
        setLoading(true);
        try {
            const rows = await query_FileNote_TagOptions.getAllWithCache();
            setOptions(Array.isArray(rows) ? rows : []);
        } catch (error) {
            console.error("Error loading file note tags:", error);
            setOptions([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const openSelector = React.useCallback(() => {
        setOpen(true);
        loadOptions();
    }, [loadOptions]);

    const toggleTag = tag => {
        const exists = selectedTags.some(t => t.toLowerCase() === tag.toLowerCase());
        const nextTags = exists
            ? selectedTags.filter(t => t.toLowerCase() !== tag.toLowerCase())
            : [...selectedTags, tag];

        onChange(nextTags.length === 0 ? null : { tags: nextTags });
    };

    const clearTags = () => {
        onChange(null);
        setOpen(false);
    };

    const label = selectedTags.length === 0
        ? "Tags"
        : selectedTags.length === 1
            ? t(selectedTags[0])
            : `Tags (${selectedTags.length})`;

    return (
        <InputGroupText>
            <span ref={anchor}>
                <Button style={btnStyle} title="Tag Filter" onClick={openSelector}>{label}</Button>
            </span>
            <AccentPopup
                className="jobWatcherPopup"
                show={open}
                onAutoClose={() => setOpen(false)}
                anchor={anchor}
                popupAlign={{ horizontal: "left", vertical: "top" }}
                anchorAlign={{ horizontal: "left", vertical: "bottom" }}
                headerContent={<strong>{t("Tag Filter")}</strong>}
            >
                <div style={{ minWidth: "260px", maxHeight: "260px", overflowY: "auto" }}>
                    <div className="acc-link" style={{ padding: "6px" }} onClick={clearTags}>{t("Clear")}</div>
                    {loading && <div style={{ padding: "6px" }}>{t("Loading...")}</div>}
                    {!loading && options.map(option => {
                        const isSelected = selectedTags.some(t => t.toLowerCase() === (option.Tag ?? "").toLowerCase());

                        return (
                            <label
                                key={option.Tag}
                                style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px", cursor: "pointer" }}
                            >
                                <Input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleTag(option.Tag)}
                                />
                                <span>{t(option.Text ?? option.Tag)}</span>
                            </label>
                        );
                    })}
                    {!loading && options.length === 0 && <div style={{ padding: "6px" }}>{t("No tags found")}</div>}
                </div>
            </AccentPopup>
        </InputGroupText>
    );
});

const AddFileNoteButton = ({ visible, disabled, isAddingNote, onClick }) => {
    if (!visible) return null;

    return (
        <Button
            onClick={onClick}
            disabled={disabled || isAddingNote}
            onMouseDown={(e) => e.preventDefault()}
            onPointerDown={(e) => e.preventDefault()}
            title={t("Add File Note")}
            icon={isAddingNote ? undefined : "plus"}
            themeColor="primary"
        >
            {isAddingNote ? <LuLoaderCircle size={18} className="animate-spin" /> : t("Add Note")}
        </Button>
    );
};

const EditFileNoteButtons = ({ onSave, onUndo }) => (
    <>
        <Button
            onClick={onSave}
            onMouseDown={(e) => e.preventDefault()}
            onPointerDown={(e) => e.preventDefault()}
            title={t("application_strings.application.buttons.save")}
            icon="save"
            themeColor="primary"
        >
            {t("application_strings.application.buttons.save")}
        </Button>
        <Button
            onClick={onUndo}
            onMouseDown={(e) => e.preventDefault()}
            onPointerDown={(e) => e.preventDefault()}
            title={t("application_strings.application.buttons.undo")}
            icon="undo"
        />
    </>
);

const selectFileNoteUser = async () => {
    const result = await showDialog(<RTLSearchSelectCtrl
        title="Insert User"
        closeTitle="application_strings.application.buttons.insert"
        searchPlaceholder="Search User"
        gridID="79664FC3-5B21-4409-B2F9-74F7CE760603"
        query={s => query_Representative_ActiveGrid.getAllAsQueryableWithCache({ search: s })}
        columns={GRIDS.Representative_Grid.Default}
        runQueryOnShow
        singleSelection
    />);

    const hasValue = !result.canceled && result.selection && result.selection.length > 0;

    if (!hasValue) return null;

    return from(result.selection).first();
};

const InsertSketch = ({ view, jobID, jobLineID, defaultSketchName, done, onInsert }) => {



    const imageNodeType = view && view.state.schema.nodes.image;
    const canInsert = view && EditorUtils.canInsert(view.state, imageNodeType);

    const handleInsert = React.useCallback(async () => {
        try {
            const selectResult = await showDialog(
                <SelectSketchDlg jobID={jobID} jobLineID={jobLineID} defaultSketchName={defaultSketchName} />
            );

            if (selectResult.canceled || accentUtils.isNull(selectResult.selected)) return;

            const data = await querySketch.getFirstOrDefaultWithCache({ id: selectResult.selected.ID });

            if (accentUtils.isEmpty(data.Sketch.Image)) return;

            const attrs = {
                src: `data:image/png;base64,${data.Sketch.Image}`,
                "data-accent-doc": data.Document.ID
            };

            onInsert(attrs);

            if (done) done();
        } catch (error) {
            console.error("Error inserting image:", error);
        }
    }, [jobID, jobLineID, defaultSketchName, done, onInsert]);


    if (accentUtils.isNull(jobID) && accentUtils.isNull(jobLineID)) return null;

    return (
        <Button
            onClick={handleInsert}
            disabled={!canInsert}
            onMouseDown={(e) => e.preventDefault()}
            onPointerDown={(e) => e.preventDefault()}
            title="Insert sketch"
            icon="image-insert"
        />
    );
};

const InsertUser = ({ jobID,done, onInsert }) => {




    const handleInsert = React.useCallback(async () => {
        try {

            const value = await selectFileNoteUser();

            if (accentUtils.isNull(value)) return;

            onInsert({ user:  value, jobID: jobID });

            if (done) done();
        } catch (error) {
            console.error("Error inserting user:", error);
        }
    }, [jobID, done, onInsert]);

    return (
        <Button
            onClick={handleInsert}
            onMouseDown={(e) => e.preventDefault()}
            onPointerDown={(e) => e.preventDefault()}
            title="Insert User"
            icon="user"
        />
    );
};


const FileNoteView = ({ fileNote }) => {

    const user = getUser();
    const elementRef = React.useRef(null);
    const ctrl = React.useRef(null);
    const [hasBeenViewed, setHasBeenViewed] = React.useState(from(fileNote.Views).any(v=>v.ID === user.ID));

    const viewCount = fileNote.Views?.length ?? 0;


    const [popoverOpen, setPopoverOpen] = React.useState(false);



    const toggle = () => {
        setPopoverOpen(true);
    };

    const close = () => {
        setPopoverOpen(false);
    };


    React.useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && !hasBeenViewed) {
                // Call server API to mark the item as viewed by the user                
                setHasBeenViewed(true);
                update("FileNote_AddView", { ParentID: fileNote.ID, ID: user.ID });
            }
        }, {
            root: null,
            rootMargin: '0px',
            threshold: 1.0
        });

        if (elementRef.current) {
            observer.observe(elementRef.current);
        }

        return () => {
            if (elementRef.current) {
                observer.unobserve(elementRef.current);
            }
        };
    }, [hasBeenViewed]);

    const content = (
        <div>

            {from(fileNote.Views).orderBy(v=>new Date(v.Date)).toArray().map((view, index) => (
                <div className="d-flex" key={index}>
                    <div>{format.formatDateTime(view.Date)}</div>
                    <div style={{ paddingLeft: "5px", paddingRight: "5px" }}>-</div>
                    <div>{view.FullName}</div>
                </div>

            ))}
        </div>
    );

    return <div ref={elementRef} style={{display: "inline-block"}}>
        <BadgeContainer >
            <AccentIcons.Eye ref={ctrl} style={{ color: "white", border: "1px solid black", borderRadius: "3px", backgroundColor: "#335fd9" }} onClick={toggle} top={7} />
            <Badge2
                position="inside"
                themeColor="success" align={{
                    vertical: "top",
                    horizontal: "end",
                }}>{viewCount}</Badge2>
        </BadgeContainer>
        <AccentPopup
            className="jobWatcherPopup"
            popupAlign={{ horizontal: "left" }}
            anchorAlign={{ horizontal: "right", vertical: "bottom" }}
            show={popoverOpen}
            onAutoClose={close}
            anchor={ctrl}
            headerContent={
                <div className="d-flex">
                    <div className="me-auto"><strong>VIEWED THIS NOTE</strong></div>
                    <div><AccentIcons.Close className="acc-btn" onClick={close} noPadding /></div>
                </div>
            }
        >
            {content}
        </AccentPopup>
    </div>;
};

export class FileNoteCtrl extends React.Component {

    constructor(props) {
        super(props);
        
        this.editor = React.createRef();

        this.state = {
            loaded: false,
            editNote: null,
            count: null,
            skip: 0,
            search: null,
            dateFilter: null,
            tagFilter: null,
            isLoadingNotes: false,
            isAddingNote: false,
            hasDraftNote: false,
            mentionOpen: false,
            mentionQuery: "",
            mentionOptions: [],
            mentionIndex: 0,
            mentionLoading: false
        };

        this.addWatchUsers = [];
        this.lastThreeChars = null;
        this.mentionQueryFetcher = new RTLQueryFetcher({
            query: query_Representative_ActiveGrid.getAllAsQueryableWithCache({ search: null })
        });

        this.searchRefreshKey = 1;

        this.scrollToViewWithOffset = this.scrollToViewWithOffset.bind(this);
//        this.onHtmlChange = this.onHtmlChange.bind(this);
        this.onHeaderExpanded = this.onHeaderExpanded.bind(this);
        this.refreshNotes = this.refreshNotes.bind(this);
        this.addNewNote = this.addNewNote.bind(this);
        this.addNote = this.addNote.bind(this);
        this.allowSketchInsert = this.allowSketchInsert.bind(this);
        this.componentDidMount = this.componentDidMount.bind(this);
        this.onEdit = this.onEdit.bind(this);
        this.onEmail = this.onEmail.bind(this);
        this.saveEditNote = this.saveEditNote.bind(this);
        this.undoEditNote = this.undoEditNote.bind(this);
        this.onInsertSketch = this.onInsertSketch.bind(this);
        this.onInsertUser = this.onInsertUser.bind(this);
        this.onSeachChange = this.onSeachChange.bind(this);
        this.onClearSearchAndDateFilter = this.onClearSearchAndDateFilter.bind(this);
        this.onDateFilterChange = this.onDateFilterChange.bind(this);
        this.onTagFilterChange = this.onTagFilterChange.bind(this);
        this.onKeyPress = this.onKeyPress.bind(this);
        this.onEditorChange = this.onEditorChange.bind(this);
        this.onEditorKeyDown = this.onEditorKeyDown.bind(this);
        this.onEditorMount = this.onEditorMount.bind(this);
        this.promptInsertUser = this.promptInsertUser.bind(this);
        this.openMentionPicker = this.openMentionPicker.bind(this);
        this.closeMentionPicker = this.closeMentionPicker.bind(this);
        this.loadMentionOptions = this.loadMentionOptions.bind(this);
        this.insertMentionUser = this.insertMentionUser.bind(this);
        this.onFileNoteTagsChanged = this.onFileNoteTagsChanged.bind(this);
    }

    componentDidMount() {
        if (this.props.noHeaderSection) {
            this.onHeaderExpanded();
        }
    }

    onKeyPress(event){

        if (event.charCode === 13) {
            this.refreshNotes();
        }
    };

    onSeachChange(value) {

        if (accentUtils.isNull(value)) {
            this.searchRefreshKey++;
        }

        this.setState({ search: value }, () => {
            if (accentUtils.isNull(value)) {
                this.refreshNotes();
            }
        });


    }

    onDateFilterChange(dateFilter) {
        this.setState({ dateFilter: dateFilter }, () => this.refreshNotes());
    }

    onTagFilterChange(tagFilter) {
        this.setState({ tagFilter: tagFilter }, () => this.refreshNotes());
    }

    onClearSearchAndDateFilter() {
        this.searchRefreshKey++;
        this.setState({ search: null, dateFilter: null, tagFilter: null }, () => this.refreshNotes());
    }

    onFileNoteTagsChanged(fileNoteID, tag) {
        this.setState(state => ({
            notes: (state.notes ?? []).map(note => {
                if (note.ID !== fileNoteID) return note;

                const hasCostcoTag = hasFileNoteTag(tag, COSTCO_FILE_NOTE_TAG);
                const syncStatus = hasCostcoTag
                    ? (note.CostcoLeadNoteSyncStatus ?? "Pending")
                    : (note.CostcoLeadNoteSyncStatus === "Synced" ? note.CostcoLeadNoteSyncStatus : null);

                return { ...note, Tag: tag, CostcoLeadNoteSyncStatus: syncStatus };
            })
        }));
    }

    allowSketchInsert() {
        return !accentUtils.isEmpty(this.props.jobID) || !accentUtils.isEmpty(this.props.jobLineID)
    }

    addNewNote(note) {
        const res = this.props.addNote(note, [...this.addWatchUsers]);

        this.addWatchUsers = [];

        return res;
    }

    onEmail(fileNote) {
        showDialog(<RTLSearchSelectCtrl
            title="Email File Note To"
            closeTitle="application_strings.application.buttons.send"
            searchPlaceholder="Search User"
            gridID="79664FC3-5B21-4409-B2F9-74F7CE760603"
            query={s => query_Representative_ActiveGrid.getAllAsQueryableWithCache({ search: s })}
            columns={GRIDS.Representative_Grid.Default}
            runQueryOnShow                        
        />).then(e => {

            if (!e.canceled && e.selection && e.selection.length > 0) {
                update("Notifications_EmailTo", {
                    ParentID: fileNote.ID,
                    IDs: from(e.selection).select(i => i.ID).toArray()
                }, true).then(r => {          
                    showOK("Emails Sent", "Emails Sent", ["application_strings.application.buttons.close"], false, null, true, 600);
                });
            }
        });

    }

    onEdit(e) {

        this.addWatchUsers = [];
        this.setState({ editNote: e }, () => {
            const view = this.editor.current.view;
            EditorUtils.setHtml(view, e.Notes);
            this.setState({ hasDraftNote: false });

        });
    }
    
    async saveEditNote() {


        const view = this.editor.current.view;
        const html = EditorUtils.getHtml(view.state);



        const res = await update("UpdateFileNote", { ID: this.state.editNote.ID, Notes: html, AddWatchUsers: [...this.addWatchUsers] }, true);

        this.addWatchUsers = [];
        EditorUtils.setHtml(view, "");

        this.setState({ editNote: null, hasDraftNote: false });
        this.refreshNotes();
    }

    onInsertSketch(e) {
        
        const view = this.editor.current.view;
        const imageNodeType = view && view.state.schema.nodes.image;

        if (view.state && view.dispatch) {
            const imageNode = imageNodeType.createAndFill(e);
            const tr = view.state.tr;
            tr.replaceSelectionWith(imageNode);
            view.dispatch(tr);
            this.setState({ hasDraftNote: true });
        } else {
            console.error('Transaction did not change the document');
        }
    }

    onInsertUser(e) {

        const view = this.editor.current.view;

        if (view.state && view.dispatch) {
            const insertFrom = e.selection?.from ?? view.state.selection.from;
            const insertTo = e.selection?.to ?? view.state.selection.to;

            const schema = view.state.schema;
            const mentionMarks = [
                schema.marks.strong.create(),
                schema.marks.style.create({ style: "color: #0d6efd;" })
            ];
            const mentionNode = schema.text(e.user.FullName, mentionMarks);

            const tr = view.state.tr
                .replaceWith(insertFrom, insertTo, mentionNode)
                .insertText(" ")
                .setStoredMarks([]);

            view.dispatch(tr);
            if (!this.addWatchUsers.includes(e.user.ID)) {
                this.addWatchUsers.push(e.user.ID);
            }
            this.setState({ hasDraftNote: true });
        }
    }

    async promptInsertUser(selection) {
        try {
            const user = await selectFileNoteUser();

            if (accentUtils.isNull(user)) return;

            this.onInsertUser({ user: user, selection: selection });
        } catch (error) {
            console.error("Error inserting user:", error);
        }
    }

    async loadMentionOptions(search = "") {
        this.setState({ mentionLoading: true });

        try {
            const safeSearch = search ?? "";
            const dataState = {
                skip: 0,
                take: 10,
                sort: [{ field: "FullName", dir: "asc" }],
                filter: accentUtils.isNullOrEmpty(safeSearch)
                    ? null
                    : {
                        logic: "and",
                        filters: [{
                            field: "FullName",
                            operator: "startswith",
                            ignoreCase: true,
                            value: safeSearch
                        }]
                    }
            };
            const res = await this.mentionQueryFetcher.fetchData(dataState);
            const rows = Array.isArray(res) ? res : (res?.data ?? []);

            this.setState({
                mentionOptions: rows,
                mentionIndex: 0,
                mentionLoading: false
            });
        } catch (error) {
            console.error("Error loading mention users:", error);
            this.setState({ mentionOptions: [], mentionLoading: false });
        }
    }

    openMentionPicker(selection) {
        this.mentionSelection = selection;
        this.setState({
            mentionOpen: true,
            mentionQuery: "",
            mentionOptions: [],
            mentionIndex: 0
        }, () => this.loadMentionOptions(""));
    }

    closeMentionPicker() {
        this.mentionSelection = null;
        this.setState({
            mentionOpen: false,
            mentionQuery: "",
            mentionOptions: [],
            mentionIndex: 0,
            mentionLoading: false
        });
    }

    insertMentionUser(user) {
        if (accentUtils.isNull(user)) return;

        const selectedUser = {
            ...user,
            ID: user.ID ?? user.RepresentativeId,
            FullName: user.FullName ?? user.RepresentativeName ?? user.Name
        };

        if (accentUtils.isNullOrEmpty(selectedUser.FullName)) return;

        const view = this.editor.current?.view;
        const selection = this.mentionSelection;
        const insertFrom = selection?.from ?? view?.state?.selection?.from;
        const insertTo = view?.state?.selection?.from ?? selection?.to ?? insertFrom;

        this.onInsertUser({ user: selectedUser, selection: { from: insertFrom, to: insertTo } });
        this.closeMentionPicker();
    }

    onEditorKeyDown(view, event) {
        if (this.state.mentionOpen) {
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                this.setState(state => ({
                    mentionIndex: event.key === "ArrowDown"
                        ? Math.min(state.mentionIndex + 1, Math.max(0, state.mentionOptions.length - 1))
                        : Math.max(state.mentionIndex - 1, 0)
                }));
                return true;
            }

            if (event.key === "Enter" || event.key === "Tab") {
                event.preventDefault();
                this.insertMentionUser(this.state.mentionOptions[this.state.mentionIndex] ?? this.state.mentionOptions[0]);
                return true;
            }

            if (event.key === "Escape") {
                event.preventDefault();
                this.closeMentionPicker();
                return true;
            }

            if (event.key === "Backspace") {
                const nextQuery = this.state.mentionQuery.slice(0, -1);
                this.setState({ mentionQuery: nextQuery }, () => this.loadMentionOptions(nextQuery));
                return false;
            }

            if (event.key.length === 1) {
                if (/\s|[.,!?]/.test(event.key)) {
                    this.closeMentionPicker();
                    return false;
                }

                const nextQuery = `${this.state.mentionQuery}${event.key}`;
                this.setState({ mentionQuery: nextQuery }, () => this.loadMentionOptions(nextQuery));
                return false;
            }
        }

        if (event.key !== "@") return false;

        event.preventDefault();

        const selection = view.state.selection;

        if (isMobileOnly) {
            this.promptInsertUser(selection ? { from: selection.from, to: selection.to } : undefined);
        } else {
            this.openMentionPicker(selection ? { from: selection.from, to: selection.to } : undefined);
        }

        return true;
    }

    onEditorMount(event) {
        const { plugins, doc } = event.viewProps.state;

        return new ProseMirror.EditorView(
            { mount: event.dom },
            {
                ...event.viewProps,
                state: EditorState.create({
                    doc,
                    plugins
                }),
                handleKeyDown: this.onEditorKeyDown
            }
        );
    }


    undoEditNote() {
        this.addWatchUsers = [];
        this.setState({ editNote: null }, () => {
            const view = this.editor.current.view;
            EditorUtils.setHtml(view, "<p></p>");
            this.setState({ hasDraftNote: false });
        });
    }

    onEditorChange(event) {
        const hasDraftNote = hasFileNoteContent(event.html);

        if (hasDraftNote !== this.state.hasDraftNote) {
            this.setState({ hasDraftNote: hasDraftNote });
        }
    }

    addNote(e) {


        if (this.editor.current) {
            const view = this.editor.current.view;

            if (view) {
                var html = EditorUtils.getHtml(view.state);

                EditorUtils.setHtml(view, "");
                if (hasFileNoteContent(html)) {
                    this.setState({ isAddingNote: true, hasDraftNote: false });
                    this.addNewNote(html).then(r => {
                        return this.refreshNotes(); 
                    }).finally(() => {
                        this.setState({ isAddingNote: false });
                    });
                } else {
                    this.setState({ hasDraftNote: false });
                }

            }
        }

    }

    onHeaderExpanded() {
        if (!this.state.loaded) {
            this.refreshNotes();
        }
    }

    refreshNotes(skip = 0, take = 20) {
        var me = this;

        this.setState({ isLoadingNotes: true });

        return this.props.loadNotes({
            skip: skip,
            take: take,
            search: me.state.search,
            dateFrom: me.state.dateFilter?.dateFrom,
            dateTo: me.state.dateFilter?.dateTo,
            tags: me.state.tagFilter?.tags?.join(",")
        }).then(function (res) {

            const newNotes = skip === 0 ? res.Notes : [...me.state.notes, ...res.Notes];

            me.setState({ notes: newNotes, show: true, loaded:true, skip: skip + (take ?? -1), count: res.Count });
        }).finally(function () {
            me.setState({ isLoadingNotes: false });
        });
    }


    scrollToViewWithOffset() {
        const view = this.editor.current.view;
        const editorRect = view.dom.getBoundingClientRect();
        const desiredScrollTop = editorRect.top + window.pageYOffset - 150;

        window.scrollTo({
            top: desiredScrollTop,
            behavior: 'auto',
        });
    }

    render() {

        const me = this;

        var details = null;


        if (!this.state.loaded) {
            details = <AccentSpinner />;
        } else {

            const items = this.state.notes.map(function (note) {

                const canEdit = canEditFileNote(note);


                const jobLineDetails = accentUtils.isNull(note.JobLineID) ? undefined : <span>Line [<A href={`/JobLine/${note.JobLineID}`} >{note.JobLine}</A>]</span>;

                return (
                    <Card key={note.ID} className="mb-2">
                        <CardBody style={{ padding: "8px 10px" }}>
                        <div style={{ fontWeight: 600, marginBottom: "4px", display: "flex", alignItems: "center", gap: "6px" }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <FileNoteView fileNote={note } />
                                {format.formatDateTime(new Date(note.Date), true, true)} - {jobLineDetails} {accentUtils.isNull(jobLineDetails) ? '' : ' - '} {note.Representative}
                                <div style={{ fontSize: "smaller", display: "inline-block" }}>&nbsp;[<div className="acc-link" onClick={e => me.onEmail(note)} >Email To</div>]</div>
                                <div style={{ fontSize: "smaller", display: "inline-block" }}>&nbsp;<AlertStatus isActive={note.Alert ?? false} fileNoteID={ note.ID } /></div>                            
                                {canEdit && <div style={{ fontSize: "smaller", display: "inline-block" }}>&nbsp;[<div className="acc-link" onClick={e => me.onEdit(note)} >Edit</div>]</div>}
                            </div>
                            <div style={{ marginLeft: "auto", whiteSpace: "nowrap" }}>
                                <FileNoteTagSelector fileNote={note} onTagsChanged={me.onFileNoteTagsChanged} />
                                <FileNoteCostcoSyncStatus fileNote={note} />
                            </div>
                        </div>
                        <div dangerouslySetInnerHTML={{ __html: note.Notes }} />
                        </CardBody>
                    </Card>);
            });

            const editing = !accentUtils.isNull(this.state.editNote);

            const tools = [
                [p => editing
                    ? <EditFileNoteButtons {...p} onSave={this.saveEditNote} onUndo={this.undoEditNote} />
                    : <AddFileNoteButton {...p} visible={this.state.hasDraftNote || this.state.isAddingNote} disabled={!this.state.hasDraftNote} isAddingNote={this.state.isAddingNote} onClick={this.addNote} />],
                [EditorTools.Bold, EditorTools.Italic, EditorTools.Underline, EditorTools.Strikethrough],
                [EditorTools.OrderedList, EditorTools.UnorderedList],
                [EditorTools.Undo],
                [p => <InsertSketch {...p} jobID={this.props.jobID} jobLineID={this.props.jobLineID} defaultSketchName={this.props.defaultSketchName} done={this.scrollToViewWithOffset} onInsert={this.onInsertSketch} />],
                [p => <InsertUser {...p} jobID={this.props.jobID} done={this.scrollToViewWithOffset} onInsert={this.onInsertUser} />]
            ];



            const btnStyle = { height: "37px" };
            
            const n = nodes;

            n.image.attrs = {
                ...n.image.attrs, "data-accent-doc": { default: null}
            };
            const customSchema = new Schema({
                marks: marks,
                nodes: n,                
            });

            const canFetchNext20 = this.state.notes.length < this.state.count;
            const canFetchNext50 = this.state.notes.length < this.state.count;
            const canFetchAll = this.state.notes.length < this.state.count;
            const mentionOptions = this.state.mentionOptions ?? [];

            details = accentUtils.isEmpty(this.props.disabled) ? <Row>
                <Col md={6}>
                    <div style={{ position: "relative" }}>
                        <Editor
                            ref={this.editor}
                            contentStyle={{ height: "300px" }}
                            defaultEditMode="div"
                            tools={tools}
                            schema={customSchema}
                            onChange={this.onEditorChange}
                            onMount={this.onEditorMount}
                        />
                        {this.state.mentionOpen && (
                            <div
                                style={{
                                    position: "absolute",
                                    left: 8,
                                    top: 42,
                                    width: "320px",
                                    maxWidth: "calc(100% - 16px)",
                                    maxHeight: "240px",
                                    overflowY: "auto",
                                    backgroundColor: "white",
                                    border: "1px solid #ccc",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                                    zIndex: 1000
                                }}
                            >
                                {this.state.mentionLoading && <div style={{ padding: "8px" }}>{t("Loading...")}</div>}
                                {!this.state.mentionLoading && mentionOptions.length === 0 && <div style={{ padding: "8px" }}>{t("No users found")}</div>}
                                {!this.state.mentionLoading && mentionOptions.map((user, index) => {
                                    const selected = index === this.state.mentionIndex;
                                    const fullName = user.FullName ?? user.RepresentativeName ?? user.Name ?? "";

                                    return (
                                        <div
                                            key={user.ID ?? user.RepresentativeId ?? index}
                                            onMouseDown={e => {
                                                e.preventDefault();
                                                this.insertMentionUser(user);
                                            }}
                                            onMouseEnter={() => this.setState({ mentionIndex: index })}
                                            style={{
                                                padding: "6px 8px",
                                                cursor: "pointer",
                                                backgroundColor: selected ? "#e9f2ff" : "white"
                                            }}
                                        >
                                            {fullName}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </Col>
                <Col md={6}>
                    <Row>
                        <Col md={12}>
                            <InputGroup className="trackingSearch" size="sm" >
                                <FileNoteDateFilterPreview btnStyle={btnStyle} dateFilter={this.state.dateFilter} onChange={this.onDateFilterChange} />
                                <FileNoteTagFilterPreview btnStyle={btnStyle} tagFilter={this.state.tagFilter} onChange={this.onTagFilterChange} />
                                <Input key={me.searchRefreshKey} onKeyPress={me.onKeyPress} onChange={e => me.onSeachChange(e.target.value)} type="text" name="search" placeholder={t("application_strings.views.tracking.searchPlaceholder")} />
                                <InputGroupText><Button style={btnStyle} onClick={this.onClearSearchAndDateFilter}>X</Button></InputGroupText>
                            </InputGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={12} className="file-note-ctrl">
                            <div>
                                {this.state.isLoadingNotes ? <AccentSpinner /> : items}
                            </div>
                        </Col>
                    </Row>   
                    <Row>
                        <Col md={12}>
                            <InputGroup className="trackingSearch" size="sm" >
                                <InputGroupText>{this.state.notes.length} of { this.state.count }</InputGroupText>                                
                                <InputGroupText><Button style={btnStyle} disabled={!canFetchNext20} onClick={() => this.refreshNotes(this.state.skip) }>Next 20</Button></InputGroupText>                                
                                <InputGroupText><Button style={btnStyle} disabled={!canFetchNext50} onClick={() => this.refreshNotes(this.state.skip, 50)}>Next 50</Button></InputGroupText>                                
                                <InputGroupText><Button style={btnStyle} disabled={!canFetchAll} onClick={() => this.refreshNotes(this.state.skip, null)}>All</Button></InputGroupText>                                
                            </InputGroup>
                        </Col>
                    </Row>
                </Col>
            </Row> : this.props.disabled;




        }


        const title = this.props.title ?? "application_strings.application.general.fileNotes";

        const fileNoteContent = <Col md={12}>
            {details}
        </Col>;

        const content = this.props.noHeaderSection ? <Row><h4>{t(title)}</h4>{fileNoteContent}</Row> : <AccentSectionHeader title={title} collapsed={true} onExpand={this.onHeaderExpanded} onIsExpandedOnLoad={this.onHeaderExpanded}>
            {fileNoteContent }
        </AccentSectionHeader>;
        
        return content;

    }

}




export const FileNoteAlert = ({ contactID, companyID, top, color = "blue" }) => {

    const fileNoteAlertContext = useFileNoteAlerts();

    const [popoverOpen, setPopoverOpen] = React.useState(false);
    const [alerts, setAlerts] = React.useState(null);
    const ctrl = React.useRef(null);

    React.useEffect(() => {


        if (fileNoteAlertContext.hasAlertsForContact(contactID)) {
            setAlerts(fileNoteAlertContext.getAlertsForContact(contactID));

            return;
        }

        if (fileNoteAlertContext.hasAlertsForCompany(companyID)) {
            setAlerts(fileNoteAlertContext.getAlertsForContact(companyID));
            return;
        }


        query_FileNoteAlertsForContactCompany.getAll({ contactID: contactID, companyID: companyID }).then(alerts => {

            setAlerts(alerts);

            fileNoteAlertContext.registerFileNoteAlerts(alerts);

            if (!accentUtils.isNull(contactID)) {
                fileNoteAlertContext.contactViewed(contactID);
            }
            if (!accentUtils.isNull(companyID)) {
                fileNoteAlertContext.companyViewed(companyID);
            }
            

        });


    }, []);

    const toggle = () => {
        setPopoverOpen(true);
    };

    const close = () => {
        setPopoverOpen(false);
    };


    if (accentUtils.isEmpty(alerts)) {
        return null;
    }

    const style = { color: color };

    const clickElement = <div className="d-flex">
        <AccentIcons.BellActive style={style} ref={ctrl} onClick={toggle} top={ top} />        
    </div>;



    return (
        <div>
            {clickElement}
            <AccentPopup
                className="jobWatcherPopup"
                popupAlign={{ horizontal: "left" }}
                anchorAlign={{ horizontal: "right", vertical: "bottom" }}
                show={popoverOpen}
                onAutoClose={close}
                anchor={ctrl}
                headerContent={
                    <div className="d-flex">
                        <div className="me-auto"><strong>ALERTS</strong></div>
                        <div><AccentIcons.Close className="acc-btn" onClick={close} noPadding /></div>
                    </div>
                }                
            >
                <AlertsDisplay alerts={ alerts} />
            </AccentPopup>
        </div>
    );

};


const AlertStatus = ({ isActive: externalIsActive, fileNoteID, size = 24 }) => {
    const [internalIsActive, setInternalIsActive] = React.useState(externalIsActive || false);
    const [isUpdating, setIsUpdating] = React.useState(false);

    React.useEffect(() => {
        if (externalIsActive !== undefined) {
            setInternalIsActive(externalIsActive);
        }
    }, [externalIsActive]);

    const handleClick = async () => {
        const newState = !internalIsActive;
        setIsUpdating(true);
        try {
            setInternalIsActive(newState);
            await update("FileNote_SetAlert", { ID: fileNoteID, Alert: newState });
        } catch (error) {
            // Revert state if update fails
            setInternalIsActive(!newState);
            console.error('Failed to update alert status:', error);
        } finally {
            setIsUpdating(false);
        }
    };

    if (isUpdating) {
        return (
            <LuLoaderCircle
                size={size}
                style={{ color: "blue", cursor: "wait" }}
                strokeWidth={2}
                className="animate-spin"
            />
        );
    }

    if (internalIsActive) {
        return (
            <LuBellRing
                size={size}
                style={{ color: "red", cursor: "pointer" }}
                strokeWidth={2}
                onClick={handleClick}
            />
        );
    }

    return (
        <LuBellOff
            size={size}
            style={{ color: "grey", cursor: "pointer" }}
            strokeWidth={2}
            onClick={handleClick}
        />
    );
};
